import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import type { ApiComponentType, ApiError, NotaryApi, PushFileValues } from '@izimi/api';
import { useModalStore, withMessages } from '@izimi/components';
import type { VaultEntity } from '@izimi/modules';
import { createNotaryModule, useInvalidateDocumentsVaultListCache } from '@izimi/modules';
import type { SecondParameter } from '@izimi/utils';

import { notaryApi, userApi } from '../../di';
import notaryMessages from './messages';

const hooks = createNotaryModule({
  userApi,
  notaryApi,
});

const useNotaryPushDocument = () => {
  const [progress, setProgress] = useState(0);
  const controller = useRef(new AbortController()).current;
  const cancelPush = () => controller.abort();
  const { mutateAsync, isPending, error } = useMutation<
    Awaited<ReturnType<NotaryApi['pushDocument']>>,
    ApiError,
    PushFileValues
  >({
    mutationFn: (params) =>
      notaryApi.pushDocument(
        params,
        ({ loaded, total }) => setProgress((loaded / total) * 100),
        controller.signal,
      ),
  });

  return {
    pushNotaryDocument: mutateAsync,
    error,
    isPending,
    cancelPush,
    progress,
  };
};

const useVaultTransferOptions = () => {
  const { showModal } = useModalStore();

  const handleClaimVault = useCallback(() => {
    showModal({ type: 'claimVault' });
  }, [showModal]);

  const handlePassClaimedVault = useCallback(
    ({
      vaultId,
      vaultName,
      notaryLastName,
    }: {
      vaultId: string;
      vaultName: string;
      notaryLastName?: string;
    }) => {
      showModal({
        type: 'passClaimedVault',
        vaultId,
        vaultName,
        notaryLastName,
      });
    },
    [showModal],
  );

  const handlePassClaimedVaultConfirmation = useCallback(
    ({
      vaultId,
      vaultName,
      notaryId,
      notaryName,
    }: {
      vaultId: string;
      vaultName: string;
      notaryId: string;
      notaryName: string;
    }) => {
      showModal({
        type: 'passClaimedVaultConfirmation',
        vaultId,
        vaultName,
        notaryId,
        notaryName,
      });
    },
    [showModal],
  );

  return {
    handleClaimVault,
    handlePassClaimedVault,
    handlePassClaimedVaultConfirmation,
  };
};

const useHeirOptions = (vaultId: string) => {
  const { showModal } = useModalStore();
  const { notaryDeleteHeir } = hooks.useNotaryDeleteHeir(vaultId);

  const showHeirDetails = useCallback(
    (heir: ApiComponentType<'Heir'>) => {
      showModal({ type: 'heirDetails', heir });
    },
    [showModal],
  );

  const handleAddHeir = useCallback(() => {
    showModal({ type: 'addHeir', vaultId });
  }, [showModal, vaultId]);

  const handleEditHeir = useCallback(
    (heir: ApiComponentType<'Heir'>) => {
      showModal({ type: 'editHeir', heir });
    },
    [showModal],
  );

  const handleDeleteHeir = useCallback(
    (heir: ApiComponentType<'Heir'>) => {
      const name = `${heir.firstName} ${heir.lastName}`;
      withMessages(notaryDeleteHeir({ heirId: heir.heirId ?? '' }))(
        notaryMessages.notaryDeleteHeir(name),
      );
    },
    [notaryDeleteHeir],
  );

  const handleTransferVault = useCallback(
    ({
      vault,
      heir,
      count,
    }: {
      vault: VaultEntity;
      heir?: ApiComponentType<'Heir'>;
      count?: number;
    }) => {
      showModal({ type: 'transferVaultConfirmation', vault, heir, count });
    },
    [showModal],
  );

  const handleRejectTransferVault = useCallback(
    ({
      heir,
      undoRejection = false,
    }: {
      heir: ApiComponentType<'Heir'>;
      undoRejection?: boolean;
    }) => {
      showModal({
        type: 'rejectVaultTransferConfirmation',
        heir,
        undoRejection,
      });
    },
    [showModal],
  );

  return {
    showHeirDetails,
    handleAddHeir,
    handleEditHeir,
    handleDeleteHeir,
    handleTransferVault,
    handleRejectTransferVault,
  };
};

const useCertification = () => {
  const useInitiateSigningFlow = () => {
    const { mutateAsync, isPending, error } = useMutation<[Blob, Headers], ApiError, any>({
      mutationFn: (params) => notaryApi.initiateSigningFlow(params),
    });

    return {
      initiateSigningFlow: mutateAsync,
      error,
      isPending,
    };
  };

  const useGetCertificationActivationToken = () => {
    const { mutateAsync, isPending, error } = useMutation<
      Awaited<ReturnType<NotaryApi['getActivationToken']>>,
      ApiError
    >({ mutationFn: () => notaryApi.getActivationToken() });

    return {
      getActivationToken: mutateAsync,
      error,
      isPending,
    };
  };

  const useValidateCertificate = (sessionId: string) => {
    const { mutateAsync, isPending, error } = useMutation<
      Awaited<ReturnType<NotaryApi['validateCertificate']>>,
      ApiError,
      string[]
    >({
      mutationFn: (params) => notaryApi.validateCertificate(sessionId, params),
    });

    return {
      validateCertificate: mutateAsync,
      error,
      isPending,
    };
  };

  const useGetHashToSign = (sessionId: string) => {
    const { mutateAsync, isPending, error } = useMutation<
      Awaited<ReturnType<NotaryApi['getHashToSign']>>,
      ApiError
    >({ mutationFn: () => notaryApi.getHashToSign({ sessionId }) });

    return {
      getHashToSign: mutateAsync,
      error,
      isPending,
    };
  };

  const useSign = (sessionId: string) => {
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();

    const { mutateAsync, isPending, error } = useMutation<
      Awaited<ReturnType<NotaryApi['sign']>>,
      ApiError,
      SecondParameter<NotaryApi['sign']>
    >({
      mutationFn: (params) => notaryApi.sign(sessionId, params),
      onSuccess: () => invalidateDocumentsVaultListCache(),
    });

    return {
      sign: mutateAsync,
      error,
      isPending,
    };
  };

  return {
    useInitiateSigningFlow,
    useGetCertificationActivationToken,
    useValidateCertificate,
    useGetHashToSign,
    useSign,
  };
};

export const notaryModule = {
  ...hooks,
  useVaultTransferOptions,
  useHeirOptions,
  useNotaryPushDocument,
  useCertification,
};

export type NotaryModule = typeof notaryModule;
